import React, { useState } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import EditIcon from '@mui/icons-material/EditOutlined'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import FieldLabel from '@/v2/components/common/FieldLabel'
import Switch from '@/v2/components/common/Switch'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsAppointmentTypes,
  useAddAppointmentType,
  useUpdateAppointmentType,
  useDeleteAppointmentType,
} from '@/v2/hooks/settings/useSettingsAppointmentTypes'
import { useSettingsRooms } from '@/v2/hooks/settings/useSettingsRooms'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import AppointmentTypeBookingRules from './AppointmentTypeBookingRules'
import {
  SettingsPage,
  PrimaryButton,
  ActionButton,
  StatusBadge,
  ColorGrid,
  ColorOption,
} from '@/v2/components/settings/shared'

const Table = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`

// Shared by the header and the rows so the two can never drift apart.
const COLUMNS = 'minmax(0, 2.5fr) 80px 100px 120px minmax(0, 3fr) 130px'

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 1.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
  font-size: 0.8125rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 1.5rem;
  padding: 0.875rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`

const NameText = styled.div`
  min-width: 0;
`

const Description = styled.div`
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
`

// The hours are what opens the editor, so the row keeps a single "Edit" (the
// appointment type itself) and the hours read as the thing they change.
const HoursButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 100%;
  margin: -0.375rem -0.5rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  background: none;
  font: inherit;
  text-align: left;
  color: #111827;
  cursor: pointer;

  svg {
    flex-shrink: 0;
    color: #9ca3af;
  }

  &:hover {
    border-color: #e5e7eb;
    background-color: #f9fafb;

    svg {
      color: #2563eb;
    }
  }
`

const HoursLines = styled.div`
  line-height: 1.5;
  white-space: nowrap;
`

const ColorDot = styled.div`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${props => props.color || '#6b7280'};
  border: 2px solid white;
  box-shadow: 0 0 0 1px #e5e7eb;
`

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// The summary names every day ("Mon, Tue, Wed, Thu and Fri") because that is how
// the patient reads it on the link. A table cell has less room, so three or more
// days in a row collapse to a range: "Mon–Fri".
const compactDays = days => {
  const indexes = days
    .split(/, | and /)
    .map(day => DAY_ORDER.indexOf(day))
    .filter(index => index >= 0)

  const runs = []
  indexes.forEach(index => {
    const run = runs[runs.length - 1]
    if (run && index === run[run.length - 1] + 1) run.push(index)
    else runs.push([index])
  })

  return runs
    .flatMap(run =>
      run.length >= 3
        ? [`${DAY_ORDER[run[0]]}–${DAY_ORDER[run[run.length - 1]]}`]
        : run.map(index => DAY_ORDER[index])
    )
    .join(', ')
}

// "Mon, Tue, Wed, Thu and Fri · 9:00 AM to 5:00 PM" → "Mon–Fri, 9:00 AM – 5:00 PM"
const compactRule = line => {
  const [days, hours = ''] = line.split(' · ')
  return `${compactDays(days)}, ${hours.replace(/ to /g, ' – ')}`
}

const HelpText = styled.p`
  margin: -0.5rem 0 0 2rem;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`

const COLORS = [
  { name: 'ivory', hex: '#bbad8f' },
  { name: 'blush', hex: '#ca8f88' },
  { name: 'cherry', hex: '#d15252' },
  { name: 'peach', hex: '#f1864b' },
  { name: 'mustard', hex: '#dcb52b' },
  { name: 'lemon', hex: '#799a4f' },
  { name: 'turquoise', hex: '#66adb6' },
  { name: 'water', hex: '#5ea3e7' },
  { name: 'lavender', hex: '#6a7bd8' },
  { name: 'violet', hex: '#9779cc' },
  { name: 'rose', hex: '#dc92c3' },
  { name: 'indigo', hex: '#77819c' },
]

const colorToHex = name => {
  const found = COLORS.find(c => c.name === name)
  return found ? found.hex : name
}

// Duration: number input, min=15, max=480, step=5 (matching Rails)

const INITIAL_FORM = {
  title: '',
  description: '',
  duration: 30,
  needsDoctor: true,
  isRemote: false,
  bookingPageShow: true,
  color: 'water',
  roomId: null,
}

const AppointmentTypes = () => {
  const {
    appointmentTypes: rawAppointmentTypes,
    isLoading,
  } = useSettingsAppointmentTypes()
  const { rooms } = useSettingsRooms()
  const { addAppointmentType, isLoading: isAdding } = useAddAppointmentType()
  const {
    updateAppointmentType,
    isLoading: isUpdating,
  } = useUpdateAppointmentType()
  const {
    deleteAppointmentType,
    isLoading: isDeleting,
  } = useDeleteAppointmentType()
  const { onOpenSnackbar } = useSnackbarContext()

  const appointmentTypes = rawAppointmentTypes.map(apt => ({
    id: apt.id,
    title: apt.title || '',
    description: apt.description || '',
    duration: apt.duration || 30,
    needsDoctor: apt.needsDoctor !== undefined ? apt.needsDoctor : true,
    isRemote: apt.isRemote || false,
    bookingPageShow:
      apt.bookingPageShow !== undefined ? apt.bookingPageShow : true,
    roomId: apt.roomId || null,
    color: apt.color || 'water',
    bookingRuleSummary: apt.bookingRuleSummary || [],
  }))

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForCreate,
    openForEdit,
    close,
    updateField,
  } = useFormModal(INITIAL_FORM)

  // Booking rules are written through their own endpoint, keyed by an appointment
  // type that already exists, so they sit outside the create and edit form rather
  // than inside it.
  const [bookingRulesTarget, setBookingRulesTarget] = useState(null)

  const isSaving = isAdding || isUpdating

  const handleDelete = async typeId => {
    if (confirm('Are you sure you want to delete this appointment type?')) {
      try {
        await deleteAppointmentType(typeId)
        onOpenSnackbar('Appointment type deleted successfully', 'success')
        close()
      } catch (error) {
        onOpenSnackbar('Failed to delete appointment type', 'error')
      }
    }
  }

  const handleToggleBookingPage = async (type, checked) => {
    try {
      await updateAppointmentType({ id: type.id, booking_page_show: checked })
      onOpenSnackbar(
        checked
          ? `${type.title} is now on the booking page`
          : `${type.title} was removed from the booking page`,
        'success'
      )
    } catch (error) {
      onOpenSnackbar('Failed to update the booking page', 'error')
    }
  }

  const handleSubmit = async () => {
    const typePayload = {
      title: formData.title,
      description: formData.description,
      duration: formData.duration,
      needs_doctor: formData.needsDoctor,
      is_remote: formData.isRemote,
      booking_page_show: formData.bookingPageShow,
      color: formData.color,
      room_id: formData.isRemote ? null : formData.roomId,
    }

    try {
      if (isEditing) {
        await updateAppointmentType({ id: editingItem.id, ...typePayload })
        onOpenSnackbar('Appointment type updated successfully', 'success')
      } else {
        await addAppointmentType(typePayload)
        onOpenSnackbar('Appointment type created successfully', 'success')
      }
      close()
    } catch (error) {
      onOpenSnackbar('Failed to save appointment type', 'error')
    }
  }

  return (
    <SettingsPage
      title="Appointment Types"
      subtitle="Manage the types of appointments patients can book"
      loading={isLoading}
      actions={
        <PrimaryButton onClick={openForCreate}>+ Add Type</PrimaryButton>
      }
    >
      <Table>
        <TableHeader>
          <div>Name</div>
          <div>Duration</div>
          <div>Doctor</div>
          <div>Booking page</div>
          <div>Reschedule link hours</div>
          <div />
        </TableHeader>

        {appointmentTypes.length === 0 ? (
          <EmptyState>
            <p>No appointment types configured yet.</p>
            <button
              onClick={openForCreate}
              style={{
                marginTop: '1rem',
                color: '#2563eb',
                background: 'none',
                border: 'none',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Create your first appointment type
            </button>
          </EmptyState>
        ) : (
          appointmentTypes.map(type => (
            <TableRow key={type.id}>
              <NameCell>
                <ColorDot color={colorToHex(type.color)} />
                <NameText>
                  <strong>{type.title}</strong>
                  {type.description && (
                    <Description>{type.description}</Description>
                  )}
                </NameText>
              </NameCell>
              <div>{type.duration} min</div>
              <div>
                <StatusBadge
                  $variant={type.needsDoctor ? 'success' : undefined}
                >
                  {type.needsDoctor ? 'Required' : 'Optional'}
                </StatusBadge>
              </div>
              <div>
                <Switch
                  label={type.bookingPageShow ? 'On' : 'Off'}
                  checked={type.bookingPageShow}
                  disabled={isUpdating}
                  onChange={(e, checked) =>
                    handleToggleBookingPage(type, checked)
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              </div>
              <div>
                {type.bookingRuleSummary.length > 0 ? (
                  <HoursButton
                    type="button"
                    title="Change reschedule link hours"
                    onClick={() => setBookingRulesTarget(type)}
                  >
                    <HoursLines>
                      {type.bookingRuleSummary.map(line => (
                        <div key={line}>{compactRule(line)}</div>
                      ))}
                    </HoursLines>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </HoursButton>
                ) : (
                  <ActionButton
                    type="button"
                    onClick={() => setBookingRulesTarget(type)}
                  >
                    + Set hours
                  </ActionButton>
                )}
              </div>
              <div style={{ justifySelf: 'end' }}>
                <ActionButton
                  onClick={() => openForEdit(type)}
                  style={{ marginRight: '0.5rem' }}
                >
                  Edit
                </ActionButton>
                <ActionButton onClick={() => handleDelete(type.id)}>
                  Delete
                </ActionButton>
              </div>
            </TableRow>
          ))
        )}
      </Table>

      <Modal
        isOpen={showModal}
        title={isEditing ? 'Edit Appointment Type' : 'New Appointment Type'}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel={isEditing ? 'Save Changes' : 'Create Type'}
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={false}
        maxWidth="sm"
        fullWidth
        showExtraButton={isEditing}
        extraButtonLabel="Delete"
        extraButtonColor="error"
        onExtraButtonClick={() => handleDelete(editingItem?.id)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={val => updateField('title', val)}
          />
          <TextField
            fullWidth
            label="Description"
            placeholder="Brief description of this appointment type"
            value={formData.description}
            onChange={val => updateField('description', val)}
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            type="number"
            label="Duration (min)"
            value={String(formData.duration)}
            onChange={val => {
              const num = parseInt(val) || 15
              updateField('duration', Math.min(480, Math.max(15, num)))
            }}
            placeholder="E.g.: 30"
          />
          <div>
            <FieldLabel>Color</FieldLabel>
            <ColorGrid $columns={6} $gap="0.5rem">
              {COLORS.map(c => (
                <ColorOption
                  key={c.name}
                  type="button"
                  color={c.hex}
                  $selected={formData.color === c.name}
                  $size="2rem"
                  $height="2rem"
                  $radius="0.25rem"
                  onClick={() => updateField('color', c.name)}
                  title={c.name}
                />
              ))}
            </ColorGrid>
          </div>
          {!formData.isRemote && rooms.length > 0 && (
            <SelectField
              fullWidth
              label="Room"
              options={[
                { label: 'No room assigned', value: '' },
                ...rooms.map(r => ({ label: r.title, value: String(r.id) })),
              ]}
              value={formData.roomId ? String(formData.roomId) : ''}
              onChange={val =>
                updateField('roomId', val ? parseInt(val) : null)
              }
            />
          )}
          <CheckboxField
            label="Requires a doctor"
            checked={formData.needsDoctor}
            onChange={e => updateField('needsDoctor', e.target.checked)}
          />
          <CheckboxField
            label="Can be conducted remotely"
            checked={formData.isRemote}
            onChange={e => updateField('isRemote', e.target.checked)}
          />
          <CheckboxField
            label="Show on the booking page"
            checked={formData.bookingPageShow}
            onChange={e => updateField('bookingPageShow', e.target.checked)}
          />
          <HelpText>
            Lets patients pick this type on the booking page. Reschedule link
            hours are set separately, from the table.
          </HelpText>
        </Box>
      </Modal>

      {bookingRulesTarget && (
        <AppointmentTypeBookingRules
          appointmentType={bookingRulesTarget}
          onClose={() => setBookingRulesTarget(null)}
        />
      )}
    </SettingsPage>
  )
}

export default AppointmentTypes
